import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Plane, AlertTriangle, BarChart3, Radio, Bell, ChevronLeft, Smartphone, HelpCircle, Shield, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { groundOpsApi } from "@/api";
import HowItWorksModal from "./HowItWorksModal";

const NAV = [
  { to: "/turnaround/board",          icon: Plane,          label: "Live Board",       testid: "nav-board"      },
  { to: "/turnaround/exceptions",     icon: AlertTriangle,  label: "Exceptions",       testid: "nav-exceptions" },
  { to: "/turnaround/stations",       icon: BarChart3,      label: "Stations",         testid: "nav-stations"   },
  { to: "/turnaround/vendors",        icon: Shield,         label: "Vendor SLA",       testid: "nav-vendors"    },
  { to: "/turnaround/staff-planning", icon: Users,          label: "Staff Planning",   testid: "nav-staff-plan" },
  { to: "/turnaround/ground-fatigue", icon: Shield,         label: "Ground Fatigue",   testid: "nav-g-fatigue"  },
  { to: "/ramp",                      icon: Smartphone,     label: "Ramp App",         testid: "nav-ramp"       },
];

export default function TurnaroundLayout() {
  const [alertCount, setAlertCount] = useState(0);
  const [showHiw, setShowHiw]       = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    groundOpsApi.getAlerts().then(a => setAlertCount(a.length)).catch(() => {});
    const t = setInterval(() => {
      groundOpsApi.getAlerts().then(a => setAlertCount(a.length)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex h-screen bg-[#07111f] text-white overflow-hidden font-sans">
      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className="w-52 flex-shrink-0 bg-[#0a1828] border-r border-orange-500/15 flex flex-col">
        {/* Brand */}
        <div className="px-4 py-4 border-b border-orange-500/10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-orange-500/20 border border-orange-500/30 rounded flex items-center justify-center">
              <Radio size={14} className="text-orange-400" />
            </div>
            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-orange-400 leading-tight">Turnaround</div>
              <div className="text-[8px] text-slate-500 uppercase tracking-widest">Control Tower</div>
            </div>
          </div>
          <div className="mt-2 text-[8px] text-slate-600 uppercase tracking-wider font-mono">Fatigue Shield · Ground Ops</div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-2 space-y-0.5 mt-1">
          {NAV.map(({ to, icon: Icon, label, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded text-xs font-semibold transition-all ${
                  isActive
                    ? "bg-orange-500/15 text-orange-400 border border-orange-500/25"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon size={12} />
              {label}
              {label === "Exceptions" && alertCount > 0 && (
                <span className="ml-auto text-[9px] bg-red-500 text-white rounded-full px-1.5 py-0.5 font-black">
                  {alertCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Seed + Back */}
        <div className="p-3 border-t border-orange-500/10 space-y-2">
          <button
            onClick={async () => { await groundOpsApi.seed(); window.location.reload(); }}
            className="w-full text-[9px] text-slate-500 hover:text-orange-400 transition py-1.5 border border-slate-700/40 rounded hover:border-orange-500/30 uppercase tracking-widest"
            data-testid="seed-btn"
          >
            Reset Demo Data
          </button>
          <button
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-1.5 text-[9px] text-slate-600 hover:text-slate-400 transition px-2 py-1"
          >
            <ChevronLeft size={9} /> Fatigue Shield
          </button>
        </div>
      </aside>

      {/* ── Main ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center justify-between px-5 py-2.5 bg-[#070f1d] border-b border-orange-500/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[9px] uppercase tracking-widest text-slate-600 font-mono">Ground Operations Control</span>
            <span className="w-1 h-1 rounded-full bg-orange-500 animate-pulse" />
            <span className="text-[9px] text-orange-400 font-mono uppercase">Live</span>
          </div>
          <div className="flex items-center gap-3">
            {alertCount > 0 && (
              <div className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
                <Bell size={9} className="text-red-400" />
                <span className="text-[9px] text-red-400 font-bold">{alertCount} Active Alerts</span>
              </div>
            )}

            {/* HOW IT WORKS — prominent button */}
            <button
              onClick={() => setShowHiw(true)}
              data-testid="how-it-works-btn"
              className="flex items-center gap-2 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/40 text-orange-400 text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg transition-all"
            >
              <HelpCircle size={12} />
              How It Works
            </button>

            <span className="text-[8px] text-slate-600 font-mono" id="clock" />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* How It Works modal */}
      {showHiw && <HowItWorksModal onClose={() => setShowHiw(false)} />}
    </div>
  );
}
